import { Box, Typography, CircularProgress, Divider, IconButton, Tooltip } from "@mui/material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { marked } from "marked";
import { extractOutput } from "./AgentUtils";
import { useState, useEffect } from "react";

interface Props {
    prompt: string;
    result: string;
}
const PromptResponse: React.FC<Props> = ({ prompt, result }) => {
    const [showPromptTimeout, setShowPromptTimeout] = useState(false);
    const [showResultTimeout, setShowResultTimeout] = useState(false);
    const [copiedPrompt, setCopiedPrompt] = useState(false);
    const [copiedResponse, setCopiedResponse] = useState(false);

    const handleCopyPrompt = async () => {
        if (prompt && prompt !== undefined) {
            try {
                await navigator.clipboard.writeText(prompt);
                setCopiedPrompt(true);
                setTimeout(() => setCopiedPrompt(false), 2000);
            } catch (err) {
                console.error("Failed to copy prompt:", err);
            }
        }
    };

    const handleCopyResponse = async () => {
        if (result && result !== undefined) {
            try {
                const textToCopy = extractOutput(result) || result;
                await navigator.clipboard.writeText(textToCopy);
                setCopiedResponse(true);
                setTimeout(() => setCopiedResponse(false), 2000);
            } catch (err) {
                console.error("Failed to copy response:", err);
            }
        }
    };

    useEffect(() => {
        const promptTimer = setTimeout(() => {
            if (prompt === undefined) {
                setShowPromptTimeout(true);
            }
        }, 3000);

        const resultTimer = setTimeout(() => {
            if (result === undefined) {
                setShowResultTimeout(true);
            }
        }, 3000);

        return () => {
            clearTimeout(promptTimer);
            clearTimeout(resultTimer);
        };
    }, [prompt, result]);


    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 3, gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, textDecoration: 'underline'}}>Prompt</Typography>
                <Tooltip title={copiedPrompt ? "Copied!" : "Copy prompt"}>
                    <IconButton 
                        size="small" 
                        onClick={handleCopyPrompt}
                        disabled={!prompt || prompt === undefined}
                        sx={{ ml: 0.5 }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ px: 3, pb: 1, width: '95%', display: 'flex', flexDirection: 'column'}}>
                {prompt === undefined ? (
                    showPromptTimeout ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Prompt not available
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Loading prompt...</Typography>
                        </Box>
                    )
                ) : (
                    <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
                        component="div"
                        dangerouslySetInnerHTML={{
                            __html: (() => {
                            try {
                                return marked.parse(prompt, { async: false });
                            } catch (e) {
                                return "<i>Error parsing prompt content.</i>";
                            }
                            })()
                        }}
                        />
                )}
                </Box>
                <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 3, pt: 1, gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, textDecoration: 'underline'  }}>Response</Typography>
                <Tooltip title={copiedResponse ? "Copied!" : "Copy response"}>
                    <IconButton 
                        size="small" 
                        onClick={handleCopyResponse}
                        disabled={!result || result === undefined}
                        sx={{ ml: 0.5 }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ px: 3, py: 1, flexGrow: 1, width: '95%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                {result === undefined ? (
                    showResultTimeout ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Agent response not available
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Loading response...</Typography>
                        </Box>
                    )
                ) : (
                    <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
                        component="div"
                        dangerouslySetInnerHTML={{
                            __html: (() => {
                            try {
                                return marked.parse(extractOutput(result), { async: false });
                            } catch (e) {
                                // If extractOutput fails, try parsing the raw result
                                try {
                                    return marked.parse(result, { async: false });
                                } catch (e2) {
                                    return "<i>Error parsing result content.</i>";
                                }
                            }
                            })()
                        }}
                        />
                )}
            </Box>
        </>
    )
}

export default PromptResponse;
